import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Users, 
  Mail, 
  Phone, 
  MapPin, 
  Globe, 
  CheckCircle2, 
  ArrowRight,
  Loader2,
  DollarSign,
  Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AffiliateRequestPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const businessId = searchParams.get("business_id");
  const businessName = searchParams.get("business_name");

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    mobile: "",
    country: "USA",
    zip_code: "",
    business_id: businessId || "",
    business_name: businessName || ""
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      await axios.post(`${API}/affiliate-request`, formData);
      setIsSuccess(true);
      
      // Redirect to website after 3 seconds
      setTimeout(() => {
        window.location.href = "https://localbest.ai";
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to submit request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-[#020817] flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-card p-8 md:p-12 rounded-2xl text-center max-w-md"
        >
          <div className="w-20 h-20 rounded-full bg-[#10B981]/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-[#10B981]" />
          </div>
          <h2 className="text-2xl font-bold font-['Outfit'] text-white mb-4">
            Request Submitted!
          </h2>
          <p className="text-[#94A3B8] mb-6">
            Thank you for your interest in our affiliate program. We will send your 
            sign-up link and portal instructions to <span className="text-[#00E5FF]">{formData.email}</span> after 
            you have been accepted.
          </p>
          <p className="text-sm text-[#64748B] mb-6">
            Redirecting you to LocalBest.ai...
          </p>
          <Button 
            className="btn-primary"
            onClick={() => window.location.href = "https://localbest.ai"}
          >
            Visit LocalBest.ai <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020817] py-12">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/20 mb-6">
            <DollarSign className="w-4 h-4 text-[#F59E0B]" />
            <span className="text-sm text-[#F59E0B]">Affiliate Program</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-white mb-4">
            Join Our <span className="gradient-text">Affiliate Program</span>
          </h1>
          <p className="text-[#94A3B8] text-lg max-w-xl mx-auto">
            Earn commissions by referring businesses to the Best of Best Directory. 
            Fill out the form below to get started.
          </p>
        </motion.div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card p-6 md:p-8 rounded-2xl"
        >
          {businessName && (
            <div className="mb-6 p-4 rounded-xl bg-[#7C3AED]/10 border border-[#7C3AED]/20">
              <p className="text-sm text-[#94A3B8]">Applying to promote:</p>
              <p className="text-white font-semibold">{businessName}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Full Name */}
            <div className="space-y-2">
              <Label htmlFor="full_name" className="text-white flex items-center gap-2">
                <Users className="w-4 h-4 text-[#00E5FF]" />
                Full Name *
              </Label>
              <Input
                id="full_name"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                placeholder="Enter your full name"
                required
                className="bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#00E5FF]" />
                Email Address *
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                required
                className="bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
              />
            </div>

            {/* Mobile */}
            <div className="space-y-2">
              <Label htmlFor="mobile" className="text-white flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#00E5FF]" />
                Mobile Number *
              </Label>
              <Input
                id="mobile"
                name="mobile"
                type="tel"
                value={formData.mobile}
                onChange={handleChange}
                placeholder="+1 (555) 123-4567"
                required
                className="bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
              />
            </div>

            {/* Country and Zip Code */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="country" className="text-white flex items-center gap-2">
                  <Globe className="w-4 h-4 text-[#00E5FF]" />
                  Country *
                </Label>
                <Input
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  placeholder="USA"
                  required
                  className="bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="zip_code" className="text-white flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#00E5FF]" />
                  Zip Code *
                </Label>
                <Input
                  id="zip_code"
                  name="zip_code"
                  value={formData.zip_code}
                  onChange={handleChange}
                  placeholder="12345"
                  required
                  className="bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                />
              </div>
            </div>

            {/* Info Box */}
            <div className="p-4 rounded-xl bg-[#10B981]/10 border border-[#10B981]/20">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-[#10B981] mt-0.5" />
                <div>
                  <p className="text-white font-medium mb-1">What happens next?</p>
                  <p className="text-sm text-[#94A3B8]">
                    After submitting, our team will review your application. Once approved, 
                    we will send your affiliate sign-up link and portal instructions to your email.
                  </p>
                </div>
              </div>
            </div>

            {error && (
              <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <Button 
              type="submit" 
              className="w-full btn-primary py-6 text-lg"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit Application
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </form>
        </motion.div>

        {/* Benefits Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-8 grid md:grid-cols-3 gap-4"
        >
          {[
            { icon: DollarSign, title: "Earn Commissions", desc: "Get paid for every referral" },
            { icon: Users, title: "Unlimited Referrals", desc: "No caps on earnings" },
            { icon: Sparkles, title: "Exclusive Resources", desc: "Marketing materials provided" }
          ].map((benefit, idx) => (
            <div key={idx} className="glass-card p-4 rounded-xl text-center">
              <benefit.icon className="w-8 h-8 text-[#F59E0B] mx-auto mb-2" />
              <h4 className="text-white font-medium text-sm">{benefit.title}</h4>
              <p className="text-[#94A3B8] text-xs">{benefit.desc}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}
